import PocketBase from 'pocketbase';

class PocketBaseService {
  constructor() {
    this.baseUrl = localStorage.getItem('pb_base_url') || 'https://db.doutor.cloud';
    this.pb = new PocketBase(this.baseUrl);
    this.pb.autoCancellation(false);
    
    // Limite máximo de itens por página permitido pelo servidor
    this.MAX_PER_PAGE = 100; 
  }

  async connect(url, email, password) {
    const cleanUrl = url.replace(/\/$/, '');
    this.pb = new PocketBase(cleanUrl);
    this.pb.autoCancellation(false);
    
    await this.pb.admins.authWithPassword(email, password);
    localStorage.setItem('pb_base_url', cleanUrl);
    
    await this.ensureCore();
    return { success: true };
  }

  async ensureCore() {
    try {
      // Usamos getList em vez de getFullList para ter controle total sobre perPage
      const collectionsResult = await this.pb.collections.getList(1, this.MAX_PER_PAGE, { 
        requestKey: null 
      });
      const names = collectionsResult.items.map(c => c.name);
      
      const defaultRules = {
        listRule: null,
        viewRule: null,
        createRule: null,
        updateRule: null,
        deleteRule: null,
      };

      if (!names.includes('greta_projects')) {
        await this.pb.collections.create({
          name: 'greta_projects',
          type: 'base',
          schema: [
            { name: 'name', type: 'text', required: true },
            { name: 'collections_list', type: 'json' },
            { name: 'description', type: 'text' }
          ],
          ...defaultRules
        });
      }

      if (!names.includes('greta_tokens')) {
        await this.pb.collections.create({
          name: 'greta_tokens',
          type: 'base',
          schema: [
            { name: 'name', type: 'text', required: true },
            { name: 'token', type: 'text', required: true },
            { name: 'project_ids', type: 'json' },
            { name: 'active', type: 'bool', default: true }
          ],
          ...defaultRules
        });
      }
    } catch (e) {
      console.error("Erro no provisionamento:", e);
    }
  }

  // MÉTODOS DE BUSCA - CORREÇÃO DE LOTE (MAX 100)
  async getGretaProjects() {
    try {
      // getFullList(batchSize) - Definimos 100 para evitar o erro 400 do servidor
      const records = await this.pb.collection('greta_projects').getFullList(this.MAX_PER_PAGE, {
        sort: '-created',
        requestKey: null,
        $autoCancel: false
      });
      return records;
    } catch (e) {
      console.error("Falha ao ler greta_projects:", e.message);
      return [];
    }
  }

  async getGretaTokens() {
    try {
      // Explicitamente limitando o batch para 100
      const records = await this.pb.collection('greta_tokens').getFullList(this.MAX_PER_PAGE, {
        sort: '-created',
        requestKey: null,
        $autoCancel: false
      });
      return records;
    } catch (e) {
      console.error("Falha ao ler greta_tokens:", e.message);
      return [];
    }
  }

  async getCollections() {
    try {
      const result = await this.pb.collections.getList(1, this.MAX_PER_PAGE, { 
        requestKey: null 
      });
      return result.items;
    } catch (e) {
      console.error("Erro ao listar coleções:", e);
      return [];
    }
  }

  // ESCRITA
  async saveGretaProject(data) {
    if (data.id) return this.pb.collection('greta_projects').update(data.id, data);
    return this.pb.collection('greta_projects').create(data);
  }

  async saveGretaToken(data) {
    if (data.id) return this.pb.collection('greta_tokens').update(data.id, data);
    return this.pb.collection('greta_tokens').create(data);
  }

  async deleteGretaToken(id) { return this.pb.collection('greta_tokens').delete(id); }
  async deleteGretaProject(id) { return this.pb.collection('greta_projects').delete(id); }

  async reconnectFromStorage() {
    if (this.pb.authStore.isValid) {
      try {
        await this.pb.admins.authRefresh();
        return true;
      } catch (e) { return false; }
    }
    return false;
  }

  getTimeUntilExpiry() {
    if (!this.pb.authStore.token) return 0;
    try {
      const payload = JSON.parse(atob(this.pb.authStore.token.split('.')[1]));
      return (payload.exp * 1000) - Date.now();
    } catch (e) { return 0; }
  }

  disconnect() {
    this.pb.authStore.clear();
    localStorage.removeItem('pb_base_url');
    window.dispatchEvent(new Event('pb_logout'));
  }

  getConnectionInfo() {
    return {
      isConnected: this.pb.authStore.isValid,
      currentUser: { email: this.pb.authStore.model?.email || 'Superuser' },
      baseUrl: this.pb.baseUrl
    };
  }

  async getCollectionSchema(name) {
    const col = await this.pb.collections.getOne(name, { requestKey: null });
    return col.schema;
  }

  getRecords(name, page = 1) {
    // Garantindo que buscas de registros também respeitem o limite do servidor
    return this.pb.collection(name).getList(page, 50, { 
      sort: '-created', 
      requestKey: null 
    });
  }

  createRecord(col, data) { return this.pb.collection(col).create(data); }
  updateRecord(col, id, data) { return this.pb.collection(col).update(id, data); }
  deleteRecord(col, id) { return this.pb.collection(col).delete(id); }

  async testConnection() {
    try {
      await this.ensureCore();
      return { provisioned: true };
    } catch (e) {
      return { provisioned: false, error: e.message };
    }
  }
}

export default new PocketBaseService();